import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FilterParams } from '../types/audit';
import { getDateRangePreset } from '../utils/dateHelpers';
import auditAPI, { CompanyOption, Environment } from '../services/api';

interface FilterPanelProps {
  onFilterChange: (filters: FilterParams) => void;
  loading?: boolean;
  environment?: Environment;
}

const FilterPanel: React.FC<FilterPanelProps> = ({ onFilterChange, loading, environment = 'prod' }) => {
  const [preset, setPreset] = useState('last_24h');
  const [startDate, setStartDate] = useState<Date>(new Date(Date.now() - 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState<Date>(new Date());
  
  const [companyId, setCompanyId] = useState('');
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [orderId, setOrderId] = useState('');

  useEffect(() => {
    auditAPI.getCompanies(environment)
      .then(setCompanies)
      .catch((err) => console.error('Failed to load companies:', err));
  }, [environment]);
  const [endpoint, setEndpoint] = useState('');
  const [method, setMethod] = useState('');
  const [username, setUsername] = useState('');
  
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [specificStatus, setSpecificStatus] = useState('');
  
  const [minResponseTime, setMinResponseTime] = useState('');
  const [maxResponseTime, setMaxResponseTime] = useState('');
  
  const [aiProvider, setAiProvider] = useState('');
  const [aiModel, setAiModel] = useState('');

  const handlePresetChange = (newPreset: string) => {
    setPreset(newPreset);
    const range = getDateRangePreset(newPreset);
    setStartDate(new Date(range.start));
    setEndDate(new Date(range.end));
  };

  const handleApplyFilters = () => {
    const filters: FilterParams = {
      dateRange: {
        start: startDate.toISOString().split('T')[0],
        end: endDate.toISOString().split('T')[0],
      },
      ...(companyId && { companyId }),
      ...(orderId && { orderId }),
      ...(endpoint && { endpoint }),
      ...(method && { method }),
      ...(username && { username }),
      ...(statusFilter !== 'all' && { statusFilter: statusFilter as any }),
      ...(specificStatus && { specificStatus: parseInt(specificStatus) }),
      ...(minResponseTime && { minResponseTime: parseInt(minResponseTime) }),
      ...(maxResponseTime && { maxResponseTime: parseInt(maxResponseTime) }),
      ...(aiProvider && { aiProvider }),
      ...(aiModel && { aiModel }),
    };

    onFilterChange(filters);
  };

  const handleReset = () => {
    setPreset('last_24h');
    setStartDate(new Date(Date.now() - 24 * 60 * 60 * 1000));
    setEndDate(new Date());
    setCompanyId('');
    setOrderId('');
    setEndpoint('');
    setMethod('');
    setUsername('');
    setStatusFilter('all');
    setSpecificStatus('');
    setMinResponseTime('');
    setMaxResponseTime('');
    setAiProvider('');
    setAiModel('');
  };

  return (
    <div style={styles.container}>
      <div style={styles.panelHeader}>Filters</div>
      
      {/* Date Range */}
      <div style={styles.section}>
        <label style={styles.label}>Date Range</label>
        <select 
          value={preset} 
          onChange={(e) => handlePresetChange(e.target.value)}
          style={styles.select}
        >
          <option value="today">Today</option>
          <option value="last_24h">Last 24 Hours</option>
          <option value="last_7d">Last 7 Days</option>
          <option value="last_30d">Last 30 Days</option>
          <option value="custom">Custom</option>
        </select>
        
        {preset === 'custom' && (
          <div style={styles.datePickerContainer}>
            <div style={{ flex: 1 }}>
              <DatePicker
                selected={startDate}
                onChange={(date: Date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                dateFormat="yyyy-MM-dd"
              />
            </div>
            <span style={styles.dateSpan}>to</span>
            <div style={{ flex: 1 }}>
              <DatePicker
                selected={endDate}
                onChange={(date: Date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                dateFormat="yyyy-MM-dd"
              />
            </div>
          </div>
        )}
      </div>

      {/* Request Filters */}
      <div style={styles.section}>
        <label style={styles.label}>Company</label>
        <select
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          style={styles.select}
        >
          <option value="">All Companies</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name || c.id}
            </option>
          ))}
        </select>
      </div>

      <div style={styles.section}>
        <label style={styles.label}>Order ID</label>
        <input
          type="text"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          placeholder="e.g., 611b5d813238dc..."
          style={styles.input}
        />
      </div>

      <div style={styles.section}>
        <label style={styles.label}>Endpoint</label>
        <input
          type="text"
          value={endpoint}
          onChange={(e) => setEndpoint(e.target.value)}
          placeholder="e.g., /recommendation"
          style={styles.input}
        />
      </div>

      <div style={styles.section}>
        <label style={styles.label}>HTTP Method</label>
        <select value={method} onChange={(e) => setMethod(e.target.value)} style={styles.select}>
          <option value="">All</option>
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PATCH">PATCH</option>
          <option value="DELETE">DELETE</option>
        </select>
      </div>

      <div style={styles.section}>
        <label style={styles.label}>Username</label>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="e.g., john.smith@example.com"
          style={styles.input}
        />
      </div>

      {/* Status Filters */}
      <div style={styles.section}>
        <label style={styles.label}>Status Filter</label>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={styles.select}>
          <option value="all">All</option>
          <option value="success">Success (2xx)</option>
          <option value="client_error">Client Error (4xx)</option>
          <option value="server_error">Server Error (5xx)</option>
        </select>
      </div>

      <div style={styles.section}>
        <label style={styles.label}>Specific Status Code</label>
        <input
          type="number"
          value={specificStatus}
          onChange={(e) => setSpecificStatus(e.target.value)}
          placeholder="e.g., 404"
          style={styles.input}
        />
      </div>

      {/* Performance Filters */}
      <div style={styles.section}>
        <label style={styles.label}>Min Response Time (ms)</label>
        <input
          type="number"
          value={minResponseTime}
          onChange={(e) => setMinResponseTime(e.target.value)}
          placeholder="e.g., 1000"
          style={styles.input}
        />
      </div>

      <div style={styles.section}>
        <label style={styles.label}>Max Response Time (ms)</label>
        <input
          type="number"
          value={maxResponseTime}
          onChange={(e) => setMaxResponseTime(e.target.value)}
          placeholder="e.g., 5000"
          style={styles.input}
        />
      </div>

      <div style={styles.section}>
        <label style={styles.label}>AI Provider</label>
        <input
          type="text"
          value={aiProvider}
          onChange={(e) => setAiProvider(e.target.value)}
          placeholder="e.g., bedrock"
          style={styles.input}
        />
      </div>

      <div style={styles.section}>
        <label style={styles.label}>AI Model</label>
        <input
          type="text"
          value={aiModel}
          onChange={(e) => setAiModel(e.target.value)}
          placeholder="e.g., amazon.nova-pro-v1:0"
          style={styles.input}
        />
      </div>

      {/* Action Buttons */}
      <div style={styles.buttonContainer}>
        <button 
          onClick={handleApplyFilters} 
          disabled={loading}
          style={{...styles.button, ...styles.primaryButton}}
        >
          {loading ? 'Loading...' : 'Apply Filters'}
        </button>
        <button 
          onClick={handleReset}
          disabled={loading}
          style={{...styles.button, ...styles.secondaryButton}}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '16px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #d8dde6',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  panelHeader: {
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: '#5a6a80',
    marginBottom: '14px',
    paddingBottom: '10px',
    borderBottom: '1px solid #d8dde6',
  },
  section: {
    marginBottom: '12px',
  },
  label: {
    display: 'block',
    marginBottom: '4px',
    fontWeight: '500',
    color: '#1a2332',
    fontSize: '12px',
    letterSpacing: '0.01em',
  },
  input: {
    width: '100%',
    padding: '7px 10px',
    border: '1px solid #d8dde6',
    borderRadius: '5px',
    fontSize: '13px',
    boxSizing: 'border-box' as const,
    color: '#1a2332',
    transition: 'border-color 0.15s',
  },
  select: {
    width: '100%',
    padding: '7px 10px',
    border: '1px solid #d8dde6',
    borderRadius: '5px',
    fontSize: '13px',
    boxSizing: 'border-box' as const,
    backgroundColor: 'white',
    color: '#1a2332',
    cursor: 'pointer',
  },
  datePickerContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px',
  },
  dateSpan: {
    fontSize: '12px',
    color: '#5a6a80',
    flexShrink: 0,
  },
  buttonContainer: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
  },
  button: {
    flex: 1,
    padding: '8px 12px',
    border: 'none',
    borderRadius: '5px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'opacity 0.15s',
    letterSpacing: '0.01em',
  },
  primaryButton: {
    backgroundColor: '#0055a5',
    color: 'white',
  },
  secondaryButton: {
    backgroundColor: '#eef1f6',
    color: '#5a6a80',
    border: '1px solid #d8dde6',
  },
};

export default FilterPanel;

